import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import AlertTriangleFilledIcon from "@redotech/redo-icons/alert-triangle_filled.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  useCanAccessCatalog,
  useCanAccessCatalogBillOfMaterials,
} from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { CatalogViews } from "./catalog-products";

export function useCatalogNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const canAccess = useCanAccessCatalog();

  if (!canAccess) {
    return null;
  }

  const friendlyName = t("Catalog", {
    context: "inventory navigation section for the merchant product catalog",
  });

  const primaryTab: LinkPrimaryTab = {
    id: "catalog",
    hasNotification: false,
    UnfilledIcon: CatalogIcon,
    FilledIcon: CatalogIconFilled,
    type: "link",
    pathRoot: routes.catalogBaseUrl,
    href: `${routes.catalogProductsHref}/${DEFAULT_CATALOG_VIEW}`,
    friendlyName,
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "catalog",
    name: friendlyName,
    NavContent: CatalogSecondaryNavigation,
  };

  return { primaryTab, secondaryNavigationData, isCore: true };
}

const CatalogSecondaryNavigation = memo(function CatalogSecondaryNavigation() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const location = useLocation();
  const canAccessBillOfMaterials = useCanAccessCatalogBillOfMaterials();
  const landedCostEnabled = useCapability("landedCost");
  const canManageFulfillment = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );

  const isProductsSelected =
    location.pathname.startsWith(`${routes.catalogProductsHref}/`) ||
    location.pathname === routes.catalogProductsHref;

  return (
    <>
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isProductsSelected}
        FilledIcon={CatalogIconFilled}
        friendlyName={t("Products")}
        id="catalog-products"
        isFolder
        UnfilledIcon={CatalogIcon}
      >
        <CatalogViews />
      </RedoSecondaryNavItem>
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${routes.catalogBaseUrl}/variants` }}
        FilledIcon={PackageIconFilled}
        friendlyName={t("Variants", {
          context: "inventory navigation label for product variants",
        })}
        id="catalog-variants"
        UnfilledIcon={PackageIcon}
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.catalogBaseUrl}/bill-of-materials`,
        }}
        FilledIcon={LayersIconFilled}
        friendlyName={t("Bill of materials", {
          context: "catalog navigation label",
        })}
        hidden={!canAccessBillOfMaterials}
        id="catalog-bill-of-materials"
        UnfilledIcon={LayersIcon}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${routes.catalogBaseUrl}/restrictions` }}
        FilledIcon={AlertTriangleFilledIcon}
        friendlyName={t("Restrictions", {
          context:
            "catalog navigation label for international import restrictions",
        })}
        hidden={!landedCostEnabled || !canManageFulfillment}
        id="catalog-restrictions"
        UnfilledIcon={AlertTriangleIcon}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${routes.catalogBaseUrl}/consignment` }}
        friendlyName={t("Consignment", {
          context:
            "inventory navigation label for consignment stock owned by a supplier but physically held and sold by the merchant",
        })}
        hidden={!(team._id === "68800d96229f8fc10f4dc1ce")}
        id="catalog-consignment"
      />
    </>
  );
});
